/* eslint-disable indent */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import moment from 'moment'
import { parseISO } from 'date-fns'
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import CloseIcon from '@mui/icons-material/Close'

import { durationTypes, simulationModes, textFieldTypes, variantTypes, dialogSize } from '../../../constants'
import { createSimulation } from '../../../actions'
import { checkErrorResponse, Logger, LoggerTypes } from '../../../utils'
import { Button, buttonModes, buttonTypes, FormTitle, Icon, iconNames, SlideToggleContent } from '../../../components'
import { SimulationConditionCard, SimulationConditionDialog } from '../../simulations'
import './style.scss'

const defaultValues = {
  start_date: null,
  mode: false, // false: 'Simple', true: Multiple, // @see `simulationModes`
  description: '',
  while_conditions: null,
  weather_impact: false,
  debug_mode: false,
  isReadOnly: false,

  // -- Time Lag
  time_lag: true,
  run_lag_duration: '0',
  run_lag_quantity: '0',
  run_lag_unit: 'HOUR', // <-- durationTypes.HOUR,

  // -- Parameter Step
  parameter_step: true,
  parameter_name: null,
  parameter_first_value: '0',
  parameter_last_value: '0',
  parameter_step_value: '0'
}

export const SimulationDialogLaunch = ({
  currentProject,
  currentScenario,
  simulation,
  simulationWhileCondition,
  parameters,
  variables,
  resources,
  setOpenDialog
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const [openCondition, setOpenCondition] = useState(false)

  const [mode, setRunMode] = useState(
    simulation?.mode === simulationModes.MULTIPLE ? true : false || defaultValues.mode
  )
  const [isReadOnly] = useState(simulation !== null)
  const [start_date, setStartDate] = useState(simulation?.start_date || defaultValues.start_date)
  const [description, setDescription] = useState(simulation?.description || defaultValues.description)
  const [while_conditions, setWhileCondition] = useState(simulationWhileCondition || defaultValues.while_conditions)
  const [weather_impact, setWeatherImpact] = useState(simulation?.weather_impact || defaultValues.weather_impact)
  const [debug_mode, setDebugMode] = useState(simulation?.debug_mode || defaultValues.debug_mode)

  // -- Time Lag
  const [time_lag, setTimeLag] = useState(simulation?.time_lag || defaultValues.time_lag)
  const [run_lag_duration, setTimeLagDuration] = useState(
    simulation?.run_lag_duration || defaultValues.run_lag_duration
  )
  const [run_quantity, setTimeLagQuantity] = useState(simulation?.run_quantity || defaultValues.run_quantity)
  const [run_lag_unit, setTimeLagUnit] = useState(simulation?.run_lag_unit || defaultValues.run_lag_unit)

  // -- Parameter Step
  const [parameter_step, setParameterStep] = useState(simulation?.parameter_step || defaultValues.parameter_step)
  const [parameter, setParameter] = useState(defaultValues.parameter)
  const parameter_name = useState(simulation?.parameter_name || defaultValues.parameter_name)
  const [parameter_first_value, setParameterInitialValue] = useState(
    simulation?.parameter_first_value || defaultValues.parameter_first_value
  )
  const [parameter_last_value, setParameterFinalValue] = useState(
    simulation?.parameter_last_value || defaultValues.parameter_last_value
  )
  const [parameter_step_value, setParameterIncrementalValue] = useState(
    simulation?.parameter_step_value || defaultValues.parameter_step_value
  )

  const [errors, setErrors] = useState(null)

  const onCloseDialog = () => setOpenDialog({ open: false })

  // --

  const _onFocus = (e) => {
    e.target.select()
  }

  const onRunModeChange = (e) => {
    e.stopPropagation()

    const new_mode = !mode

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onRunModeChange()', {
      new_mode: new_mode === true ? simulationModes.MULTIPLE : simulationModes.SINGLE
    })

    setRunMode(new_mode)
    // Debug mode is not available on MULTIPLE_RUN
    if (new_mode === true) {
      setDebugMode(false)
    }
  }

  const onDescriptionChange = (e) => {
    e.stopPropagation()
    const _value = e.target.value

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onDescriptionChange()', {
      description: _value
    })

    setDescription(_value)
  }

  const onStartDateChange = async (_value) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onStartDateChange()', { start_date: _value })
    await setErrors(null)
    await setStartDate(_value)
  }

  const onConditionClick = (e) => {
    e.stopPropagation()
    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onConditionClick()', {})
    setOpenCondition(true)
  }

  const onConditionSave = async (newCondition) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onConditionSave()', { while_conditions, newCondition })
    await setErrors(null)
    await setWhileCondition(newCondition)
  }

  const onConditionDelete = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onConditionDelete()', { while_conditions })
    setWhileCondition(null)
  }

  const onWeatherImpactChange = (e) => {
    e.stopPropagation()

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onWeatherImpactChange()', {
      weather_impact: !weather_impact
    })

    setWeatherImpact(!weather_impact)
  }

  const onDebugModeChange = (e) => {
    e.stopPropagation()

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onDebugModeChange()', {
      debug_mode: !debug_mode
    })

    setDebugMode(!debug_mode)
  }

  // -- Time Lag

  const onTimeLagChange = (e) => {
    e.stopPropagation()

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onTimeLagChange()', { time_lag: !time_lag })

    setTimeLag(!time_lag)
  }

  const onTimeLagDurationChange = (e) => {
    e.stopPropagation()
    const _value = e.target.value

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onTimeLagDurationChange()', {
      run_lag_duration: _value
    })

    setTimeLagDuration(_value)
  }

  const onTimeLagQuantityChange = (e) => {
    e.stopPropagation()
    const _value = e.target.value

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onTimeLagQuantityChange()', {
      run_quantity: _value
    })

    setTimeLagQuantity(_value)
  }

  const onTimeLagUnitChange = (e) => {
    e.stopPropagation()
    const _value = e.target.value

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onTimeLagUnitChange()', { run_lag_unit: _value })

    setTimeLagUnit(_value)
  }

  // -- Parameters

  const onParameterStepChange = (e) => {
    e.stopPropagation()

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onParameterStepChange()', {
      parameter_step: !parameter_step
    })

    setParameterStep(!parameter_step)
  }

  const onParameterChange = (e) => {
    e.stopPropagation()
    const _value = e.target.value

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onParameterChange()', {
      parameter: _value
    })
    setParameter(_value)
  }

  const onParameterInitialValueChange = (e) => {
    e.stopPropagation()
    const _value = e.target.value

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onParameterInitialValueChange()', {
      parameter_first_value: _value
    })

    setParameterInitialValue(_value)
  }

  const onParameterFinalValueChange = (e) => {
    e.stopPropagation()
    const _value = e.target.value

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onParameterFinalValueChange()', {
      parameter_last_value: _value
    })

    setParameterFinalValue(_value)
  }

  const onParameterIncrementalValueChange = (e) => {
    e.stopPropagation()
    const _value = e.target.value

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onParameterIncrementalValueChange()', {
      parameter_step_value: _value
    })

    setParameterIncrementalValue(_value)
  }

  // --

  const onSave = (e) => {
    e.stopPropagation()

    const pid = currentProject?.id || null
    const sid = currentScenario?.id || null

    const _mode = mode ? simulationModes.MULTIPLE : simulationModes.SINGLE

    let newSimulation = {
      mode: _mode,
      start_date: moment(start_date).format('YYYY-MM-DD'),
      description: description ? description : null,
      // -- in the future it will be a list of conditions so we place them in an array of objects.
      while_conditions: [
        {
          left_operand_id: while_conditions?.left_operand?.attribute?.id || null,
          operator: while_conditions?.operator || null,
          right_operand_id: while_conditions?.right_operand?.attribute?.id || null,
          right_operand_value: while_conditions?.right_operand_value || null
        }
      ],
      weather_impact,
      debug_mode
    }

    // -- Time Lag
    if (time_lag === true && _mode === simulationModes.MULTIPLE) {
      newSimulation.run_lag_duration = run_lag_duration
      newSimulation.run_quantity = run_quantity
      newSimulation.run_lag_unit = run_lag_unit
    }

    // -- Parameter Step
    if (parameter_step === true && _mode === simulationModes.MULTIPLE) {
      newSimulation.parameter_id = parameter?.id || null
      newSimulation.parameter_first_value = parameter_first_value
      newSimulation.parameter_last_value = parameter_last_value
      newSimulation.parameter_step_value = parameter_step_value
    }

    setErrors(null)

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onSave()', { newSimulation, time_lag, parameter_step })

    dispatch(createSimulation(pid, sid, newSimulation))
      .then((res) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'SimulationDialogLaunch.onSave().dispatch(createSimulation(...)).then()', {
          res
        })
        // checkSnackbarOK(enqueueSnackbar, t, res)
        onCloseDialog()
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, 'SimulationDialogLaunch.onSave().dispatch(createSimulation(...)).catch()', {
          res
        })

        checkErrorResponse(res, enqueueSnackbar, t)

        // -- check while_condition errors
        // -- check all other errors
        if (res?.response?.data?.fields) {
          setErrors({
            ...res.response.data.fields,
            while_conditions: res?.response?.data?.fields?.while_conditions?.[0]
              ? `${res.response.data.fields.while_conditions[0]?.left_operand?.[0] || ''} ${
                  res.response.data.fields.while_conditions[0]?.right_operand?.[0] || ''
                }`
              : null
          })
        }
      })
  }

  Logger.trace(LoggerTypes.CONTAINER, 'SimulationDialogLaunch.render()', {
    currentProject,
    currentScenario,
    simulation,
    parameters,
    variables,
    resources,
    simulations: {
      mode,
      start_date,
      weather_impact,
      while_conditions,
      // -- Time Lag
      time_lag,
      run_lag_duration,
      run_quantity,
      run_lag_unit,
      // -- Parameter Step
      parameter_step,
      parameter,
      parameter_first_value,
      parameter_last_value,
      parameter_step_value
    },
    errors
  })

  return (
    <Dialog
      open={true}
      maxWidth={dialogSize.EXTRA_LARGE}
      width={dialogSize.EXTRA_LARGE}
      onClose={onCloseDialog} // <-- allow to close the popin while clicking on background
      aria-labelledby="form-dialog-title"
      className="SimulationDialogLaunch"
    >
      <DialogContent>
        <FormTitle
          title={t(simulation === null ? 'simulations.launch.title' : 'simulations.launch.title_parameters')}
          style={{ fontSize: '20px' }}
        />
        <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }}>
          {/* === COLUMN 01 =========================================================================== */}
          <Grid item xs={0} sm={0} md={5} className="column columnSpacer">
            {/*  mode ('Single' | 'Multiple')  */}
            <Grid item xs={0} sm={0} className="common_fullwidth columnTitle">
              {t('simulations.launch.mode_name')}
              <Switch disabled={simulation !== null} checked={mode} onChange={onRunModeChange} />
              <span className={`mode mode-${!mode ? simulationModes.SINGLE : simulationModes.MULTIPLE}`}>
                {mode ? t('simulations.launch.mode.multi_run') : t('simulations.launch.mode.single_run')}
              </span>
            </Grid>
            {/*  start_date  */}
            <Grid item xs={0} sm={0} className="common_fullwidth lineRow" style={{ paddingTop: '5px' }}>
              <FormControl error={Boolean(errors && errors?.start_date)}>
                <LocalizationProvider dateAdapter={AdapterDateFns} className="common_fullwidth">
                  <DatePicker
                    disabled={isReadOnly}
                    label={t('simulations.launch.start_date.label')}
                    placeholder={t('simulations.launch.start_date.placeholder')}
                    value={start_date === null ? null : parseISO(start_date)}
                    autoFocus={true}
                    onFocus={_onFocus}
                    variant={variantTypes.OUTLINED}
                    onChange={onStartDateChange}
                    renderInput={(params) => <TextField {...params} />}
                    className="common_fullwidth"
                    error={Boolean(errors && errors?.start_date)}
                    helperText={(errors && errors?.start_date) || ''}
                  />
                </LocalizationProvider>
                <FormHelperText>{(errors && errors?.start_date) || ''}</FormHelperText>
              </FormControl>
            </Grid>
            {/*  description  */}
            <Grid item xs={0} sm={0} className="common_fullwidth lineRow">
              <TextField
                inputProps={{ readOnly: isReadOnly }}
                type={textFieldTypes.TEXT}
                name="description"
                id="description"
                // eslint-disable-next-line no-eq-null
                value={description}
                variant={variantTypes.OUTLINED}
                label={t('simulations.launch.description.label')}
                className="common_fullwidth"
                onChange={onDescriptionChange}
                multiline={true}
                rows={4}
                placeholder={t('simulations.launch.description.placeholder')}
                error={Boolean(errors && errors?.description)}
                helperText={(errors && errors?.description) || ''}
              />
            </Grid>
            {/*  while_conditions  */}
            <Grid item xs={0} sm={0} className="common_fullwidth lineRow">
              <FormControl error={Boolean(errors && errors?.while_conditions)} className="common_fullwidth">
                {simulation === null ? (
                  <Button
                    icon={<Icon name={iconNames.CONDITION} size={18} />}
                    onClick={onConditionClick}
                    text={t('simulations.launch.while_conditions.label')}
                    tooltip={t('simulations.launch.while_conditions.placeholder')}
                    className="common_fullwidth"
                  />
                ) : (
                  <span></span>
                )}

                <div className="selectedCondition" onClick={onConditionClick}>
                  {while_conditions ? (
                    <SimulationConditionCard currentCheck={while_conditions} onDelete={onConditionDelete} />
                  ) : null}
                </div>
                <FormHelperText>{(errors && errors?.while_conditions) || ''}</FormHelperText>
              </FormControl>
            </Grid>
            {/*  weather_impact  */}
            <Grid item xs={0} sm={0} className="common_fullwidth" style={{ marginTop: '10px' }}>
              <Checkbox disabled={isReadOnly} checked={weather_impact} onChange={onWeatherImpactChange} />
              <span>{t('simulations.launch.weather_impact')}</span>
            </Grid>
            {/*  debug_mode  */}
            <Grid item xs={0} sm={0} className="common_fullwidth" style={{ marginTop: '-10px' }}>
              <Checkbox
                disabled={simulation !== null || mode}
                checked={debug_mode}
                onChange={onDebugModeChange}
                error={Boolean(errors && errors?.debug_mode)}
                helperText={(errors && errors?.debug_mode) || ''}
              />
              <span>{t('simulations.launch.debug_mode.label')}</span>
              <Button
                icon={<Icon name={iconNames.ABOUT} size={12} />}
                tooltip={t('simulations.launch.debug_mode.tooltip')}
                className="mode info"
              />
            </Grid>
          </Grid>

          {/* === COLUMN 02 =========================================================================== */}
          <Grid item xs={0} sm={0} md={2} className="column columnSpacer">
            {/*  time_lag (true/false)  */}
            <Grid item xs={0} sm={0} className="common_fullwidth columnTitle">
              {t('simulations.launch.time')}
              <Switch disabled={simulation !== null || !mode} checked={time_lag} onChange={onTimeLagChange} />
              {time_lag ? (
                <span className={`mode mode-${simulationModes.MULTIPLE}`} style={!mode ? { opacity: '0.3' } : null}>
                  {t('simulations.launch.mode.time_lag')}
                </span>
              ) : null}
            </Grid>

            {/*  SLIDER  >  time_lag  */}
            <Grid item xs={0} sm={0} className="common_fullwidth">
              <div className="columnHider">
                <SlideToggleContent isOpen={!time_lag || !mode} wise="top" className="common_fullwidth">
                  {/*  run_lag_duration  */}
                  <Grid item xs={0} sm={0} className="common_fullwidth lineRow">
                    <TextField
                      inputProps={{ readOnly: isReadOnly }}
                      type={textFieldTypes.NUMBER}
                      name="run_lag_duration"
                      id="run_lag_duration"
                      value={run_lag_duration}
                      variant={variantTypes.OUTLINED}
                      onFocus={_onFocus}
                      label={t('simulations.launch.time_lag_duration.label')}
                      className="common_fullwidth"
                      onChange={onTimeLagDurationChange}
                      placeholder={t('simulations.launch.time_lag_duration.placeholder')}
                      error={Boolean(errors && errors?.run_lag_duration)}
                      helperText={(errors && errors?.run_lag_duration) || ''}
                    />
                  </Grid>

                  {/*  run_quantity  */}
                  <Grid item xs={0} sm={0} className="common_fullwidth lineRow">
                    <TextField
                      inputProps={{ readOnly: isReadOnly }}
                      type={textFieldTypes.NUMBER}
                      name="run_quantity"
                      id="run_quantity"
                      value={run_quantity}
                      variant={variantTypes.OUTLINED}
                      onFocus={_onFocus}
                      label={t('simulations.launch.time_lag_quantity.label')}
                      className="common_fullwidth"
                      onChange={onTimeLagQuantityChange}
                      placeholder={t('simulations.launch.time_lag_quantity.placeholder')}
                      error={Boolean(errors && errors?.run_quantity)}
                      helperText={(errors && errors?.run_quantity) || ''}
                    />
                  </Grid>

                  {/*  run_lag_unit  */}
                  <Grid item xs={0} sm={0} className="common_fullwidth">
                    <FormControl error={Boolean(errors && errors?.run_lag_unit)} className="common_fullwidth">
                      <InputLabel>{t('simulations.launch.time_lag_unit.label')}</InputLabel>
                      <Select
                        inputProps={{ readOnly: isReadOnly }}
                        value={run_lag_unit}
                        defaultValue={durationTypes.HOUR}
                        className="common_fullwidth"
                        onChange={onTimeLagUnitChange}
                        variant={variantTypes.OUTLINED}
                        label={t('simulations.launch.time_lag_unit.label')}
                      >
                        <MenuItem value={durationTypes.HOUR}>{t('simulations.launch.time_lag_unit.hour')}</MenuItem>
                        <MenuItem value={durationTypes.DAY}>{t('simulations.launch.time_lag_unit.day')}</MenuItem>
                        <MenuItem value={durationTypes.WEEK}>{t('simulations.launch.time_lag_unit.week')}</MenuItem>
                        <MenuItem value={durationTypes.MONTH}>{t('simulations.launch.time_lag_unit.month')}</MenuItem>
                      </Select>
                      <FormHelperText>{(errors && errors?.run_lag_unit) || ''}</FormHelperText>
                    </FormControl>
                  </Grid>
                </SlideToggleContent>
              </div>
            </Grid>
          </Grid>

          {/* === COLUMN 03 =========================================================================== */}
          <Grid item xs={0} sm={0} md={5} className="column">
            {/*  parameter_step (true/false)  */}
            <Grid item xs={0} sm={0} className="common_fullwidth columnTitle">
              {t('simulations.launch.step')}
              <Switch
                disabled={simulation !== null || !mode}
                checked={parameter_step}
                onChange={onParameterStepChange}
              />
              {parameter_step ? (
                <span className={`mode mode-${simulationModes.MULTIPLE}`} style={!mode ? { opacity: '0.3' } : null}>
                  {t('simulations.launch.mode.parameter_step')}
                </span>
              ) : null}
            </Grid>

            {/*  SLIDER  >  parameter_step  */}
            <Grid item xs={0} sm={0} className="common_fullwidth">
              <div className="columnHider">
                <SlideToggleContent isOpen={!parameter_step || !mode} wise="top" className="common_fullwidth">
                  {/*  parameter  */}
                  <Grid item xs={0} sm={0} className="common_fullwidth lineRow">
                    <FormControl error={false} className="common_fullwidth">
                      <InputLabel>{t('simulations.launch.parameter.label')}</InputLabel>
                      <Select
                        inputProps={{ readOnly: isReadOnly }}
                        value={parameter || 'NONE'}
                        className="common_fullwidth"
                        onChange={onParameterChange}
                        variant={variantTypes.OUTLINED}
                        label={t('simulations.launch.parameter.label')}
                      >
                        {isReadOnly ? (
                          <MenuItem value={'NONE'}>{parameter_name}</MenuItem>
                        ) : (
                          <MenuItem value={'NONE'}>{t('simulations.launch.parameter.empty_select')}</MenuItem>
                        )}
                        {!isReadOnly &&
                          parameters &&
                          parameters.map((_parameter, index) => (
                            <MenuItem key={`simulations-parameter-${index}`} value={_parameter}>
                              <span className="name">{_parameter.name}</span>{' '}
                              <span className="value">
                                ({_parameter.value} {_parameter.unit})
                              </span>
                            </MenuItem>
                          ))}
                      </Select>
                      <FormHelperText>{(errors && errors?.parameter_id) || ''}</FormHelperText>
                    </FormControl>
                  </Grid>

                  {/*  parameter_first_value  */}
                  <Grid item xs={0} sm={0} className="common_fullwidth lineRow">
                    <TextField
                      inputProps={{ readOnly: isReadOnly }}
                      type={textFieldTypes.NUMBER}
                      name="parameter_first_value"
                      id="parameter_first_value"
                      value={parameter_first_value}
                      variant={variantTypes.OUTLINED}
                      onFocus={_onFocus}
                      label={t('simulations.launch.parameter_initial_value.label')}
                      className="common_fullwidth"
                      onChange={onParameterInitialValueChange}
                      placeholder={t('simulations.launch.parameter_initial_value.placeholder')}
                      error={Boolean(errors && errors?.parameter_first_value)}
                      helperText={(errors && errors?.parameter_first_value) || ''}
                    />
                  </Grid>

                  {/*  parameter_last_value  */}
                  <Grid item xs={0} sm={0} className="common_fullwidth lineRow">
                    <TextField
                      inputProps={{ readOnly: isReadOnly }}
                      type={textFieldTypes.NUMBER}
                      name="parameter_last_value"
                      id="parameter_last_value"
                      value={parameter_last_value}
                      variant={variantTypes.OUTLINED}
                      onFocus={_onFocus}
                      label={t('simulations.launch.parameter_final_value.label')}
                      className="common_fullwidth"
                      onChange={onParameterFinalValueChange}
                      placeholder={t('simulations.launch.parameter_final_value.placeholder')}
                      error={Boolean(errors && errors?.parameter_last_value)}
                      helperText={(errors && errors?.parameter_last_value) || ''}
                    />
                  </Grid>

                  {/*  parameter_step_value  */}
                  <Grid item xs={0} sm={0} className="common_fullwidth lineRow">
                    <TextField
                      inputProps={{ readOnly: isReadOnly }}
                      type={textFieldTypes.NUMBER}
                      name="parameter_step_value"
                      id="parameter_step_value"
                      value={parameter_step_value}
                      variant={variantTypes.OUTLINED}
                      onFocus={_onFocus}
                      label={t('simulations.launch.parameter_incremental_value.label')}
                      className="common_fullwidth"
                      onChange={onParameterIncrementalValueChange}
                      placeholder={t('simulations.launch.parameter_incremental_value.placeholder')}
                      error={Boolean(errors && errors?.parameter_step_value)}
                      helperText={(errors && errors?.parameter_step_value) || ''}
                    />
                  </Grid>
                </SlideToggleContent>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      {simulation === null ? (
        <DialogActions>
          <Button
            mode={buttonModes.SECONDARY_ACTION}
            type={buttonTypes.BUTTON}
            text={t('common.cancel')}
            icon={<CloseIcon />}
            onClick={onCloseDialog}
          />
          <Button
            mode={buttonModes.PRIMARY_ACTION}
            type={buttonTypes.BUTTON}
            text={t('common.launch')}
            icon={<Icon name={iconNames.LAUNCH} size={15} />}
            onClick={onSave}
          />
        </DialogActions>
      ) : (
        <span></span>
      )}
      <SimulationConditionDialog
        variables={variables}
        resources={resources}
        parameters={parameters}
        openDialog={openCondition}
        setOpenDialog={setOpenCondition}
        onSave={onConditionSave}
        defaultCondition={while_conditions}
      />
    </Dialog>
  )
}

SimulationDialogLaunch.displayName = 'SimulationDialogLaunch'

SimulationDialogLaunch.propTypes = {
  currentProject: PropTypes.object.isRequired,
  currentScenario: PropTypes.object.isRequired,
  setOpenDialog: PropTypes.func.isRequired,
  simulation: PropTypes.object,
  simulationWhileCondition: PropTypes.object,
  // -- Scenario's Attributes
  parameters: PropTypes.arrayOf(PropTypes.object),
  variables: PropTypes.arrayOf(PropTypes.object),
  resources: PropTypes.arrayOf(PropTypes.object)
}

SimulationDialogLaunch.defaultProps = {
  currentProject: null,
  currentScenario: null,
  setOpenDialog: () => {},
  simulation: null,
  simulationWhileCondition: null,
  // -- Scenario's Attributes
  parameters: [],
  resources: [],
  variables: []
}
